<template>
  <f7-popup class="chat-bot-popup">
    <f7-view>
      <f7-page class="chat chat-with-bot">
        <f7-navbar>
          <f7-nav-left>
            <f7-link @click="exit">
              {{ $t(`close`) }}
            </f7-link>
          </f7-nav-left>
          <f7-nav-title><b>Real</b>lang Bot</f7-nav-title>
          <f7-nav-right>
            <f7-link href="/balance" class="user-balance">
              {{ `⭐️ ${$store.state.user.info.coin}` }}
            </f7-link>
          </f7-nav-right>
        </f7-navbar>

        <f7-messagebar
          ref="messagebar"
          :placeholder="`${this.$t(`page_chat.message`)}...`"
          @keydown.enter.native.exact.prevent
          @keyup.enter.native.exact="send"
          @keydown.enter.shift.native.exact
        >
          <span slot="inner-end" class="messagebar-inner-end">
            <f7-link
              class="ripple"
              icon-only
              icon-f7="rectangle_stack_fill"
              icon-color="blue"
              icon-size="30"
              @click="actionOpened = true"
            ></f7-link>

            <f7-link
              class="ripple"
              icon-only
              icon-f7="arrow_up_circle_fill"
              icon-color="blue"
              icon-size="36"
              @click="send"
            ></f7-link>
          </span>
        </f7-messagebar>

        <f7-messages ref="messages">
          <f7-message
            v-for="(message, index) in messageData"
            :key="index"
            :type="message.type"
            :first="isFirstMessage(message, index)"
            :last="isLastMessage(message, index)"
            :tail="isTailMessage(message, index)"
            :avatar="message.type === 'received' ? avatar : ''"
          >
            <span v-if="message.text" slot="text" v-html="message.text"></span>

            <!-- IMAGE TEMPLATE -->
            <span v-if="message.image" slot="image">
              <img
                :src="message.image"
                alt="description image"
                @click="openPhotoBrowser(message.image)"
              />
            </span>

            <span v-if="message.actions" slot="default" class="chat-with-bot-action-button">
              <f7-button
                v-for="(action, index2) in message.actions"
                :key="index2"
                fill
                @click="messageSendThrottling(action.title)"
              >
                {{ action.title }}
                <img v-if="action.image" :src="`${BASE_PATH}${action.image}`" :alt="action.title" />
              </f7-button>
            </span>

            <!-- DATE TEMPLATE -->
            <span slot="end" class="message-time">
              {{ message.date }}
            </span>
          </f7-message>

          <f7-message
            v-if="typing"
            type="received"
            :typing="true"
            :first="true"
            :last="true"
            :tail="true"
            :avatar="avatar"
          ></f7-message>
        </f7-messages>

        <f7-actions id="chatActions" :opened="actionOpened" @actions:closed="actionOpened = false">
          <f7-actions-group>
            <f7-actions-label>
              {{ $t(`page_chat.chat_bot_comands`) }}
            </f7-actions-label>
            <f7-actions-button bold @click="changeCategory">
              {{ $t(`page_chat.change_category`) }}
            </f7-actions-button>
          </f7-actions-group>
          <f7-actions-group>
            <f7-actions-button color="red">
              {{ $t(`cancel`) }}
            </f7-actions-button>
          </f7-actions-group>
        </f7-actions>
      </f7-page>
    </f7-view>
  </f7-popup>
</template>

<script>
import { BASE_PATH } from '@/js/const/index.js';
import { prettyDate } from '@/js/helpers/date.js';
import notify from '@/js/helpers/notify.js';
import apiRequest from '@/js/helpers/apiRequest.js';

import avatar from '@/static/bot.png';

const sentMessage = {
  type: 'sent',
  text: '',
  date: null
};

const receivedMessage = {
  type: 'received',
  text: '',
  image: null,
  actions: null,
  date: null
};

export default {
  name: 'ChatBot',

  data() {
    return {
      avatar,
      messageData: [],
      actionOpened: false,
      typing: null,
      isNewChat: true
    };
  },

  computed: {
    BASE_PATH: () => BASE_PATH,

    photos() {
      return this.messageData.filter(message => message.image).map(message => message.image);
    }
  },

  mounted() {
    this.messagebar = this.$refs.messagebar.f7Messagebar;
    this.messages = this.$refs.messages.f7Messages;

    this.messageSendThrottling('🤖 👋');
  },

  methods: {
    isFirstMessage(message, index) {
      const previousMessage = this.messageData[index - 1];
      if (message.isTitle) return false;
      if (
        !previousMessage ||
        previousMessage.type !== message.type ||
        previousMessage.name !== message.name
      )
        return true;
      return false;
    },

    isLastMessage(message, index) {
      const nextMessage = this.messageData[index + 1];
      if (message.isTitle) return false;
      if (!nextMessage || nextMessage.type !== message.type || nextMessage.name !== message.name)
        return true;
      return false;
    },

    isTailMessage(message, index) {
      const nextMessage = this.messageData[index + 1];
      if (message.isTitle) return false;
      if (!nextMessage || nextMessage.type !== message.type || nextMessage.name !== message.name)
        return true;
      return false;
    },

    async messageSendThrottling(text) {
      this.messagebar.setValue(text);

      await setTimeout(() => {
        this.send();
      }, 500);
    },

    changeCategory() {
      this.messageSendThrottling('stop');
    },

    createPhotoBrowser() {
      this.photoBrowserId = this.$f7.photoBrowser.create({
        theme: 'dark',
        photos: this.photos
      });
    },

    async openPhotoBrowser(src) {
      const index = this.photos.indexOf(src);

      if (this.photoBrowserId) {
        this.photoBrowserId.destroy();

        await this.$nextTick();
        this.createPhotoBrowser();
        await this.$nextTick();
        this.photoBrowserId.open(index);
      } else {
        this.createPhotoBrowser();
        await this.$nextTick();
        this.photoBrowserId.open(index);
      }
    },

    async send() {
      this.messagebar.focus();

      const text = this.messagebar.getValue();
      const prettyText = text.replace(/\n/g, '<br>').trim();

      if (!text.length) {
        return;
      }

      this.messagebar.clear();

      this.messageData.push({
        ...sentMessage,
        text: prettyText,
        date: prettyDate(new Date(), 'HH:mm:ss')
      });
      this.typing = {
        typing: true
      };

      try {
        const body = new FormData();
        body.append('text', prettyText);

        if (this.isNewChat) {
          body.append('is_first', this.isNewChat);
        }

        const res = await apiRequest('bot/message', 'POST', body, true);

        this.typing = null;
        this.isNewChat = false;

        if (res.error) {
          notify({ text: res.error });
          this.messagebar.setValue(text);
          return;
        }

        if (res.success) {
          const { message, select, image } = res.success;

          this.messageData.push({
            ...receivedMessage,
            text: message,
            date: prettyDate(new Date(), 'HH:mm:ss')
          });

          if (image) {
            this.messageData.push({
              ...receivedMessage,
              image,
              date: prettyDate(new Date(), 'HH:mm:ss')
            });
          }

          if (select) {
            this.messageData.push({
              ...receivedMessage,
              actions: [...select],
              date: prettyDate(new Date(), 'HH:mm:ss')
            });

            setTimeout(() => {
              document.body.style.height = '';
            }, 500);
          }
        }
      } catch (error) {
        this.messagebar.setValue(text);
        notify({ text: this.$t(`notify.something_went_wrong`) });
        this.typing = null;
      }
    },

    async exit() {
      this.$f7.popup.close();
    }
  }
};
</script>

<style lang="less">
.chat.chat-with-bot {
  --f7-message-received-bg-color: #f5f6f8;

  .messagebar {
    .messagebar-area {
      flex-shrink: initial;
    }

    a.link.icon-only {
      &:last-child {
        margin-right: 0 !important;
      }
    }
  }
}

.chat-with-bot-action-button {
  --f7-button-padding-horizontal: 20px;

  .button {
    width: 217px !important;
    display: flex;
    flex-direction: column;
    min-height: 29px;
    height: auto;

    &:not(:last-of-type) {
      margin-bottom: 4px;
    }
  }

  img {
    max-width: 100%;
    width: 100%;
    height: auto;
    margin-bottom: 20px;
    border-radius: 12px;
  }
}
</style>
